import { api } from './client';
import type { Role } from './session';

export type User = {
  id: string;
  username: string;
  displayName: string;
  role: Role;
  disabled: boolean;
  createdAt: number;
};

export type NewUser = { username: string; displayName: string; role: Role; password: string };
export type UserPatch = Partial<Pick<User, 'displayName' | 'role' | 'disabled'>> & {
  password?: string;
};

const path = (id: string) => `/api/users/${encodeURIComponent(id)}`;

export const listUsers = () => api.get<User[]>('/api/users');
export const createUser = (user: NewUser) => api.post<User>('/api/users', user);
export const updateUser = (id: string, patch: UserPatch) => api.put<User>(path(id), patch);
export const deleteUser = (id: string) => api.del<{ ok: true }>(path(id));

// 64 characters, a power of two: byte & 63 picks one with no modulo bias, unlike a set whose
// size does not divide 256.
const PASSWORD_CHARS = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789-_';

// A password handed over once when an account is created or reset. 20 chars comfortably clears
// the server's 12-character minimum (see MIN_PASSWORD_LENGTH in apps/server/src/auth/limits.ts).
export function generatePassword(length = 20): string {
  const bytes = crypto.getRandomValues(new Uint8Array(length));
  return Array.from(bytes, (b) => PASSWORD_CHARS[b & 63]).join('');
}

// Whether the target of a user-management action is the acting admin's own account: that case
// needs a different flow (own password change confirmed twice, then sign-out) than acting on
// someone else's account.
export const isSelfTarget = (targetId: string, sessionUserId: string | undefined) =>
  targetId === sessionUserId;
